import { computed, inject } from "vue";
import {
  checkRenderElementShowStatus,
  cssTextToJsObjWithBindData,
  filterConfigByScript,
} from "@/utils/renderBinding";
import { useResponsiveConfig } from "./useResponsiveConfig";
import { localizeAssetFields } from "@/utils/lowCodeLocalizedAsset";
import { upgradeHttpImageFields } from "@/utils/clientImageUrl.js";

function resolveConfigSource(configSource) {
  return typeof configSource === "function" ? configSource() : configSource;
}

export function useRenderConfig(configSource) {
  const { locale } = useI18n();
  const getConfigJson = inject("getConfigJson", () => ({}));
  const getCommonConfigJson = inject("getCommonConfigJson", () => ({}));

  const commonConfig = computed(() => {
    return typeof getCommonConfigJson === "function" ? getCommonConfigJson() : {};
  });

  const pageConfig = computed(() => {
    return typeof getConfigJson === "function" ? getConfigJson() : {};
  });

  const bindData = computed(() => {
    return {
      ...(commonConfig.value?.bindData || {}),
      ...(pageConfig.value?.bindData || {}),
    };
  });

  const baseConfig = computed(() => resolveConfigSource(configSource) || {});

  const { currentBreakpoint, resolvedConfig } = useResponsiveConfig(
    () => baseConfig.value,
  );

  const filterConfig = computed(() => {
    const config = resolvedConfig.value || baseConfig.value;
    const localizedConfig = localizeAssetFields(
      config,
      ["backgroundImage", "src", "hoverSrc", "poster"],
      locale.value,
    );
    const renderConfig = upgradeHttpImageFields(
      localizedConfig,
      ["backgroundImage", "src", "hoverSrc", "poster"],
    );
    if (!renderConfig.filterScript) {
      return renderConfig;
    }
    return filterConfigByScript(
      renderConfig.filterScript,
      bindData.value,
      renderConfig,
    );
  });

  const showStatus = computed(() => {
    if (pageConfig.value?.isPreview || commonConfig.value?.isPreview) {
      return true;
    }
    if (filterConfig.value?.__responsiveHidden) {
      return false;
    }
    return checkRenderElementShowStatus(bindData.value, filterConfig.value || {});
  });

  const setStyle = computed(() => {
    return cssTextToJsObjWithBindData(filterConfig.value?.styles || "", bindData.value);
  });

  return {
    bindData,
    filterConfig,
    setStyle,
    showStatus,
    currentBreakpoint,
  };
}
